/* eslint-disable @typescript-eslint/no-explicit-any */

import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Space, Table as AntTable } from "antd";
import { Paginated, Params } from "@feathersjs/feathers";
import {
  SorterResult,
  TablePaginationConfig,
  FilterValue,
  TableCurrentDataSource,
} from "antd/es/table/interface";
import { SearchOutlined } from "@ant-design/icons";

// import { InputSearch } from "core/common/input-search";

import { TableProps } from "./types";
import { useFetch, useDebounce } from "../hooks";
import { useLocation, useSearchParams } from "react-router-dom";

export interface FindMethod<T> {
  params: Params[];
  result: Paginated<T>;
}

function parsePositiveIntParam(v: string | null): number | undefined {
  if (v == null || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function parseNonNegativeIntParam(v: string | null): number | undefined {
  if (v == null || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

const getPageSizeStorageKey = (pathname: string) =>
  `admin_table_pageSize:${pathname}`;

function getPersistedPageSize(
  searchParams: URLSearchParams,
  pathname: string,
  fallbackLimit?: number
): number {
  const fromUrl = parsePositiveIntParam(searchParams.get("pageSize"));
  if (fromUrl) return fromUrl;

  try {
    const stored = localStorage.getItem(getPageSizeStorageKey(pathname));
    if (stored) {
      const n = Number(stored);
      if (Number.isFinite(n) && n > 0) return n;
    }
  } catch {
    // ignore localStorage errors
  }

  return fallbackLimit && fallbackLimit > 0 ? fallbackLimit : 10;
}

function savePageSizePreference(pathname: string, pageSize: number) {
  try {
    localStorage.setItem(getPageSizeStorageKey(pathname), String(pageSize));
  } catch {
    // ignore localStorage errors
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Table = <T extends object & { id: number } = any>({
  service,
  columns,
  limit,
  pagination = true,
  fetch$ClientProps,
  fetchQueryProps,
  className,
  onChange,
  onFetch,
  onLoad,
  ...restProps
}: TableProps<T>) => {
  // States
  const [isMounted, setIsMounted] = useState(false);
  const [inputValue, _setInputValue] = useState<string | undefined>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const persistedPageSize = getPersistedPageSize(
    searchParams,
    location.pathname,
    limit
  );
  const [paginationData, setPaginationData] = useState({
    total: 0,
    skip: parseNonNegativeIntParam(searchParams.get("skip")) ?? 0,
    limit: persistedPageSize,
    currentPage: parsePositiveIntParam(searchParams.get("currentPage")) ?? 1,
  });

  // Restaurar pageSize en la URL tras recargar (p. ej. /products?pageSize=100)
  useEffect(() => {
    if (searchParams.get("pageSize")) return;

    const storedSize = getPersistedPageSize(searchParams, location.pathname, limit);
    if (storedSize === (limit || 10)) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("pageSize", String(storedSize));
        if (!next.get("skip")) next.set("skip", "0");
        if (!next.get("currentPage")) next.set("currentPage", "1");
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  // useEffect(() => {
  //   setSearchParams({ id: "1234" })
  // }, [])

  // console.log("1===>",searchParams.get("id"))
  // Hooks
  const {
    fetch: fetchData,
    data: dataFetch,
    loading: loadingFetch,
  } = useFetch<FindMethod<T>>({ service: service.find });

  const { value: searchValue, loading: loadingSearch } = useDebounce(
    inputValue,
    1000
  );

  const fetchQueryKey = useMemo(
    () => JSON.stringify(fetchQueryProps ?? {}),
    [fetchQueryProps]
  );

  const getColumnInputSearchProps = useCallback(
    (dataIndex: string, type: "text" | "date" | "byID") => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            autoFocus
            placeholder="Buscar..."
            style={{ marginBottom: 8, display: "block" }}
            type={type}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm({ closeDropdown: true })}
          />
          <Space>
            <Button
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
              type="primary"
              onClick={() => confirm({ closeDropdown: true })}
            >
              Buscar
            </Button>
            <Button
              size="small"
              style={{ width: 90 }}
              onClick={() => {
                clearFilters();
                confirm({ closeDropdown: false });
              }}
            >
              Reiniciar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value: any, record: any) => {
        return record[dataIndex]
          ? record[dataIndex]
              .toString()
              .toLowerCase()
              .includes(value.toLowerCase())
          : "";
      },
      onFilterDropdownVisibleChange: (visible: boolean) => {
        if (visible) {
          setTimeout(() => /*  this.searchInput.select() */ undefined, 100);
        }
      },
    }),
    []
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const _fetch = (skip?: number, params?: any) => {
    const baseLimit = getPersistedPageSize(
      searchParams,
      location.pathname,
      paginationData.limit || limit
    );
    const skipFromUrl = parseNonNegativeIntParam(searchParams.get("skip"));
    const baseSkip =
      skip !== undefined && skip !== null ? skip : skipFromUrl ?? 0;

    const query = _.merge(
      {},
      {
        $limit: baseLimit,
        $skip: baseSkip,
        ...(fetchQueryProps || {}),
        $client: {
          ...(searchValue ? { search: searchValue } : {}),
          ...(fetch$ClientProps || {}),
        },
      },
      params || {}
    );

    fetchData({ query }).then((res) => {
      onFetch && onFetch(res);
    });
  };

  const getSortData = useCallback(
    (sorter: SorterResult<T>[] | SorterResult<T>) => {
      return (!Array.isArray(sorter) ? [sorter] : sorter)
        .map((it) =>
          it.field
            ? { [it.field as string]: it.order === "ascend" ? 1 : -1 }
            : {}
        )
        .reduce((acc, item) => {
          const key = Object.keys(item)[0];

          if (key) acc[key] = item[key];

          return acc;
        }, {});
    },
    []
  );

  const getFilterData = useCallback(
    (filters: Record<string, FilterValue | null>) => {
      const $clientFiltersKeys: string[] = [];
      const queryFilters = Object.keys(filters)
        .map((key) => {
          const colConfig = columns?.find((it) => it.dataIndex === key);
          let val: any;

          if (filters[key] !== null) {
            if (colConfig?.filterWay === "$client") {
              val = { [key]: filters[key] };
              $clientFiltersKeys.push(key);
            } else if (colConfig?.filterType === "date") {
              val = {
                [key]: {
                  $gte: `${filters[key]} 00:00:00`,
                  $lte: `${filters[key]} 23:59:59`,
                },
              };
            } else if (colConfig?.filterType === "text"){
              val = { [key]:`${filters[key]}`};
              // val = { [key]: { $like: `%${filters[key]}%` } };
            } else if (colConfig?.filterType === "data_value"){
              // val = { [key]:  `%${filters[key]}%` }
              val = { "data_value":  `%${filters[key]}%` };
            }
            else if (colConfig?.filterType === "filterByID"){
              val = { [key]:`${filters[key]}`};
            }
            else val = { [key]: { $in: filters[key] } };
          }
          else val = {};

          return val;
        })
        .reduce((acc, item) => {
          const key = Object.keys(item)[0];

          if (key) acc[key] = item[key];

          return acc;
        }, {});

      return {
        queryFilters: Object.keys(queryFilters).reduce((acc: any, item) => {
          if (!$clientFiltersKeys.includes(item))
            acc[item] = queryFilters[item];

          return acc;
        }, {}),
        $clientFilters: Object.keys(queryFilters).reduce((acc: any, item) => {
          if ($clientFiltersKeys.includes(item)) acc[item] = queryFilters[item];

          return acc;
        }, {}),
      };
    },
    [columns]
  );

  const handleChange = (
    pag: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T>[] | SorterResult<T>,
    extra: TableCurrentDataSource<T>
  ) => {
    const newLimit = pag?.pageSize || paginationData.limit;
    const newPage = pag?.current || 1;
    setPaginationData((prev) => ({
      ...prev,
      currentPage: newPage,
      limit: newLimit,
    }));

    const { queryFilters, $clientFilters } = getFilterData(filters);
    const skipVal = (newPage - 1) * newLimit;
    savePageSizePreference(location.pathname, newLimit);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("skip", skipVal.toString());
      next.set("currentPage", newPage.toString());
      next.set("pageSize", newLimit.toString());
      return next;
    });
    _fetch(skipVal, {
      ...queryFilters,
      $limit: newLimit,
      $client: {
        filterBy: $clientFilters,
      },
      $sort: getSortData(sorter),
    });

    if (onChange) onChange(pag, filters, sorter, extra);
  };
  // Sincronizar paginación con la respuesta y la URL.
  // Si solo cambia la URL (p. ej. nuevo pageSize), hay que respetarla aunque
  // dataFetch aún sea de la petición anterior; si no, el límite vuelve al valor viejo.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!dataFetch) return;

    const urlCurrent = parsePositiveIntParam(searchParams.get("currentPage"));
    const urlSkip = parseNonNegativeIntParam(searchParams.get("skip"));

    setPaginationData((prev) => ({
      ...prev,
      currentPage: urlCurrent ?? prev.currentPage,
      total: dataFetch.total,
      skip: urlSkip ?? dataFetch.skip ?? prev.skip,
      limit: getPersistedPageSize(searchParams, location.pathname, prev.limit),
    }));
  }, [dataFetch, searchParams, location.pathname]);
  //console.log({ dataFetch });
  // Fetch data when filters/query change (stable key avoids missed refetches)
  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true);
      _fetch(Number(searchParams.get("skip")) || 0);
      return;
    }
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    _fetch(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, service, fetch$ClientProps, fetchQueryKey]);

  useEffect(() => {
    if (onLoad) {
      onLoad({
        refresh: ()=> _fetch( Number(searchParams.get("skip")) || 0 ),
        // refresh: _fetch,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // console.log("x====>",dataFetch?.data)
  return (
    <div style={{ width: "100%" }}>
      {/* <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <InputSearch placeholder="Buscar..." value={inputValue} onChange={(e) => setInputValue(e.target.value)} />
            </Space> */}
      <div style={{ margin: "15px 0" }}>
        <AntTable<T>
          className={`${className || ""} tableStyleOne modern-pagination`}
          columns={columns?.map((col: any) => ({
            ...col,
            ...(col.filterType && col.filterType !== "base"
              ? { ...getColumnInputSearchProps(col.dataIndex, col.filterType) }
              : {}),
          }))}
          dataSource={dataFetch?.data || []}
          loading={loadingFetch || loadingSearch}
          pagination={
            pagination && {
              defaultCurrent: 1,
              current: paginationData.currentPage,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} elementos`,
              pageSize: paginationData.limit,
              total: paginationData.total,
              position: ["bottomCenter"],
              locale: {
                items_per_page: "por página",
              },
              style: {
                marginTop: "24px",
                textAlign: "center",
              },
              onShowSizeChange: (_current, size) => {
                savePageSizePreference(location.pathname, size);
                setPaginationData((prev) => ({
                  ...prev,
                  currentPage: 1,
                  limit: size,
                  skip: 0,
                }));
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set("pageSize", String(size));
                  next.set("skip", "0");
                  next.set("currentPage", "1");
                  return next;
                });
                _fetch(0, { $limit: size });
              },
            }
          }
          rowKey={(record) =>
            `row_table_key_${
              record.id || Math.random() * (paginationData.limit - 0) + 0
            }`
          }
          sticky
          onChange={handleChange}
          {...restProps}
        />
      </div>
    </div>
  );
};

export default Table;

